import { Component, ContentChild, DestroyRef, HostBinding, Input, output, signal, ViewChild } from '@angular/core';
import { AGMedia, AGMediaKeys, Headshot, IAspectRatio, ImageCropperPayload, MediaSize } from '@ag-common-lib/public-api';
import { ToastrService } from 'ngx-toastr';
import { AgMediaUploaderModalComponent } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.component';
import { MediaUploaderTabs } from 'ag-common-svc/lib/components/ag-media-uploader/ag-media-uploader-modal.models';
import { ImageCropperModalComponent } from 'ag-common-svc/lib/components/image-cropper-modal/image-cropper-modal.component';
import {
  revokeCropperObjectUrl,
  waitForCropperImage,
} from 'ag-common-svc/lib/components/image-cropper-modal/image-cropper-modal.utils';
import { WasabiImgPipe } from 'ag-common-svc/shared/pipes/wasabi-img.pipe';
import { AgentMergeQueueGuardService } from 'ag-common-svc/lib/services/agent-merge-queue-guard.service';
import { MergeInProgressAlertModalService } from 'ag-common-svc/lib/components/merge-in-progress-alert-modal/merge-in-progress-alert-modal.service';

@Component({
  selector: 'ag-shr-profile-picture',
  templateUrl: './profile-picture.component.html',
  styleUrls: ['./profile-picture.component.scss'],
  providers: [WasabiImgPipe],
})
export class ProfilePictureComponent {
  @HostBinding('class') className = 'profile-picture';
  @ViewChild(AgMediaUploaderModalComponent, { static: false }) mediaUploaderComponent: AgMediaUploaderModalComponent; // ViewChild for DxTooltipComponent
  @ViewChild(ImageCropperModalComponent, { static: false }) imageCropperModalComponent: ImageCropperModalComponent; // ViewChild for DxTooltipComponent
  @ContentChild('uploaderRef', { read: AgMediaUploaderModalComponent })
  customUploaderComponent: AgMediaUploaderModalComponent;

  @Input() mediaSources: MediaUploaderTabs[];
  @Input() isRound: boolean = true;
  @Input() maintainAspectRatio: boolean = true;
  @Input() maintainPreviewAspectRatio: boolean = false;
  @Input() ratio: IAspectRatio = { w: 3, h: 4 };
  @Input() isReadOnly: boolean = false;
  @Input() canEdit: boolean = false;
  @Input() canAdd: boolean = false;
  @Input() canDelete: boolean = false;
  @Input() showDropDownMenu: boolean = true;
  @Input() headshot: Headshot;
  @Input() mediaPathPrefix: string;
  @Input() mediaItems: AGMedia[];
  @Input() agentDbId: string | null = null;
  @Input() showImageCropperAfterSave = true;
  @Input() useCustomMediaUploader = false;
  @Input() onProfileImageChanged: (mediaUrl: AGMedia) => Promise<any>;
  @Input() onImageCropped: (payload: any) => Promise<void>;

  deleteImage = output<void>();

  protected readonly isPreparingCropperSig = signal(false);
  private cropperObjectUrl: string | null = null;

  readonly MediaSize = MediaSize;
  protected dropDownItems = [
    { icon: 'mdi mdi-upload', text: 'Add Picture', action: 'add', visible: false },
    { icon: 'mdi mdi-upload', text: 'Change Picture', action: 'change', visible: false },
    { icon: 'mdi mdi-pencil', text: 'Edit Thumbnail', action: 'crop', visible: false },
  ];

  constructor(
    private wasabiImgPipe: WasabiImgPipe,
    private agentMergeQueueGuardService: AgentMergeQueueGuardService,
    private mergeInProgressAlertModalService: MergeInProgressAlertModalService,
    private toastrService: ToastrService,
    destroyRef: DestroyRef,
  ) {
    destroyRef.onDestroy(() => {
      revokeCropperObjectUrl(this.cropperObjectUrl);
    });
  }

  ngOnChanges(): void {
    this.updateDropDownItems();
  }

  protected onUploadClicked = async (): Promise<void> => {
    if (this.customUploaderComponent) {
      this.customUploaderComponent.showModal();
      return;
    }
    this.mediaUploaderComponent?.showModal();
  };

  protected onMediaChanged = async (mediaUrl: AGMedia) => {
    const wasabiPath = mediaUrl?.[AGMediaKeys.wasabiPath];
    if (!wasabiPath) {
      return;
    }

    if (this.showImageCropperAfterSave) {
      this.isPreparingCropperSig.set(true);
    }

    try {
      await this.onProfileImageChanged(mediaUrl);

      // Mutate in place so we stay bound to the parent headshot reference.
      // Replacing with a shallow copy detaches from the parent and blocks crop preview updates.
      this.headshot = Object.assign(this.headshot ?? {}, { wasabiPath });
      this.updateDropDownItems();

      if (!this.showImageCropperAfterSave) {
        return;
      }

      const pictureUrl = this.wasabiImgPipe.transform(wasabiPath) ?? '';
      const cropperImageUrl = await waitForCropperImage(pictureUrl);
      if (!cropperImageUrl) {
        this.toastrService.error('Unable to load the uploaded image. Please try Edit Thumbnail again.');
        return;
      }

      revokeCropperObjectUrl(this.cropperObjectUrl);
      this.cropperObjectUrl = cropperImageUrl;
      this.imageCropperModalComponent.showModal(cropperImageUrl);
    } finally {
      this.isPreparingCropperSig.set(false);
    }
  };

  protected handleImageCropped = async (payload: ImageCropperPayload): Promise<void> => {
    if (!this.onImageCropped || !this.headshot) {
      return;
    }

    await this.onImageCropped(payload);

    Object.assign(this.headshot, { imageCropperPayload: payload });
    this.toastrService.success('Image Successfully Cropped!');
  };

  protected handleCropImage = async () => {
    this.imageCropperModalComponent.showModal(
      this.wasabiImgPipe.transform(this?.headshot?.wasabiPath),
      this?.headshot?.imageCropperPayload,
    );
  };

  protected handleDeleteImage() {
    this.deleteImage.emit();
  }

  protected handleDropDownItemClick = async (e: any): Promise<void> => {
    if (!(await this.ensureMergeGuard())) {
      return;
    }

    switch (e?.itemData?.action) {
      case 'add':
      case 'change':
        await this.onUploadClicked();
        break;
      case 'crop':
        await this.handleCropImage();
        break;
      default:
        break;
    }
  };

  private updateDropDownItems(): void {
    this.dropDownItems = [
      {
        icon: 'mdi mdi-upload',
        text: 'Add Picture',
        action: 'add',
        visible: !this.headshot?.wasabiPath && this.canAdd,
      },
      {
        icon: 'mdi mdi-upload',
        text: 'Change Picture',
        action: 'change',
        visible: !!this.headshot?.wasabiPath && this.canEdit,
      },
      {
        icon: 'mdi mdi-pencil',
        text: 'Edit Thumbnail',
        action: 'crop',
        visible: !!this.headshot?.wasabiPath && this.canEdit,
      },
    ];
  }

  private ensureMergeGuard = async (): Promise<boolean> => {
    if (!this.agentDbId) {
      return true;
    }

    try {
      const isBlocked = await this.agentMergeQueueGuardService.isAgentMergeBlocked(this.agentDbId);
      if (isBlocked) {
        this.mergeInProgressAlertModalService.open();
        return false;
      }
      return true;
    } catch {
      return true;
    }
  };
}
